import * as React from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { RemoveScroll } from 'react-remove-scroll';
import { X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useIsMobile } from '@/hooks/useIsMobile';
import { cn } from '@/lib/utils';

/**
 * Shared container for S-8 field popovers.
 * - Desktop (≥ lg): anchored shadcn Popover.
 * - Mobile (< lg): full-width bottom sheet with backdrop (PWA-friendly, fat-finger safe).
 *
 * `trigger` must be a single focusable element (the FieldBox button).
 */
export function FieldSheet({
  open,
  onOpenChange,
  trigger,
  title,
  children,
  desktopWidth = 'w-[340px]',
  align = 'start',
  testId,
}) {
  const isMobile = useIsMobile();

  if (!isMobile) {
    return (
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent
          align={align}
          sideOffset={8}
          onEscapeKeyDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => { if (e.key === 'Escape') e.stopPropagation(); }}
          className={cn(desktopWidth, 'p-0 rounded-2xl border-slate-200 shadow-xl')}
          data-testid={testId ? `${testId}-popover` : undefined}
        >
          {title ? (
            <div className="px-4 pt-4 pb-3 border-b border-slate-100">
              <div className="text-sm font-semibold text-slate-900">{title}</div>
            </div>
          ) : null}
          <RemoveScroll forwardProps removeScrollBar={false}>
            <div className="p-4">{children}</div>
          </RemoveScroll>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <>
      {React.cloneElement(trigger, { onClick: () => onOpenChange(!open) })}
      {createPortal(
        <AnimatePresence>
          {open ? (
            <motion.div
              className="fixed inset-0 z-[130] flex flex-col justify-end"
              style={{ pointerEvents: 'auto' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              data-testid={testId ? `${testId}-sheet` : undefined}
            >
              <div
                className="absolute inset-0 bg-slate-900/40 backdrop-blur-[1px]"
                onClick={() => onOpenChange(false)}
              />
              <motion.div
                className="relative bg-white rounded-t-3xl shadow-[0_-10px_40px_rgba(0,0,0,0.15)] max-h-[80vh] flex flex-col pb-[max(env(safe-area-inset-bottom),1rem)]"
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              >
                <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-slate-100">
                  <div className="text-base font-semibold text-slate-900">{title}</div>
                  <button
                    type="button"
                    onClick={() => onOpenChange(false)}
                    className="h-8 w-8 -mr-1 flex items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 transition-colors"
                    aria-label="Close"
                    data-testid={testId ? `${testId}-sheet-close` : undefined}
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <RemoveScroll forwardProps removeScrollBar={false}>
                  <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 py-4">{children}</div>
                </RemoveScroll>
              </motion.div>
            </motion.div>
          ) : null}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}

export default FieldSheet;
